import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrganizationInfo } from '../../../../types/types';
import OrganizationGrid from '../../components/OrganizationGrid';
import { SkeletonCell } from '../../../../components/SkeletonCell';


interface OrganizationListingProps {
  contactOrganizationsData?: { organizations?: ApiContactOrganization[] };
  isLoading?: boolean;
  isFetching?: boolean;
}

type ApiContactOrganizationRole = {
  id: number;
  roleId: number;
  roleName: string;
};

type ApiOrganization = {
  organizationId?: number;
  isPrimary?: boolean;
  id: number;
  name: string;
  email?: string | null;
  website?: string | null;
  image: string | null;
  signedUrl: string;
  industry: string | null;
  createdBy?: { firstName?: string; lastName?: string } | null;
  account?: { firstName?: string; lastName?: string } | null;
  legacyId: string | null;
  createdAt: string;
  roles?: ApiContactOrganizationRole[];
  source?: string | null;
};

type ApiContactOrganization = {
  contactOrgId: number;
  organizationId: number;
  isPrimary: boolean;
  organization: ApiOrganization;
  roles: ApiContactOrganizationRole[];
};

const OrganizationListing: React.FC<OrganizationListingProps> = ({
  contactOrganizationsData,
  isLoading = false,
  isFetching = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToPreviewPage = (page: string, id?: string) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page);

    if (id) {
      params.set('id', id);
    } else {
      params.delete('id');
    }

    const nextSearch = params.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const organizations = useMemo<OrganizationInfo[]>(() => {
    const apiItems = Array.isArray(contactOrganizationsData?.organizations)
      ? (contactOrganizationsData?.organizations as ApiContactOrganization[])
      : [];

    return apiItems.map((item) => {
      const org = (item.organization ?? item) as ApiOrganization;
      const roleNames = Array.isArray(item.roles)
        ? item.roles.map((role) => role.roleName).filter(Boolean)
        : Array.isArray(org.roles)
        ? org.roles.map((role) => role.roleName).filter(Boolean)
        : [];

      const creator = org?.createdBy ?? org?.account;
      const createdBy = creator
        ? `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim()
        : '';

      return {
        name: org?.name ?? 'N/A',
        email: org?.email || 'N/A',
        website: org?.website || 'N/A',
        organizationId: String(item?.organizationId ?? org?.organizationId ?? org?.id),
        logoUrl: org?.signedUrl || undefined,
        industry: org?.industry ?? 'N/A',
        creationDate: org?.createdAt ? new Date(org.createdAt).toLocaleDateString() : 'N/A',
        createdBy: createdBy || 'N/A',
        isPrimary: Boolean(item.isPrimary ?? org?.isPrimary),
        source: org?.source ?? 'N/A',
      };
    });
  }, [contactOrganizationsData]);

  const handleViewDetails = (org: OrganizationInfo) => {
    const organizationId = org.organizationId;
    if (!organizationId) {
      return;
    }

    navigateToPreviewPage('organizationDetails', encodeURIComponent(organizationId));
  };

  return (
    <div>
      {isLoading || isFetching ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
            gap: '24px',
            height: 'fit-content',
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`org-skeleton-${index}`}
              style={{
                background: 'white',
                borderRadius: 12,
                padding: 24,
                border: '1px solid #f3f4f6',
              }}
            >
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <SkeletonCell height="64px" width="64px" rounded />
                <div style={{ flex: 1 }}>
                  <SkeletonCell height="16px" width="50%" style={{ marginBottom: 10 }} />
                  <SkeletonCell height="14px" width="65%" />
                </div>
              </div>

              <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <SkeletonCell height="13px" width="80%" style={{ marginBottom: 8 }} />
                <SkeletonCell height="13px" width="70%" style={{ marginBottom: 8 }} />
                <SkeletonCell height="13px" width="75%" style={{ marginBottom: 8 }} />
                <SkeletonCell height="13px" width="60%" style={{ marginBottom: 8 }} />
                <SkeletonCell height="13px" width="55%" />
              </div>

              <SkeletonCell height="40px" width="100%" />
            </div>
          ))}
        </div>
      ) : (
        <OrganizationGrid
          organizations={organizations}
          onViewDetails={handleViewDetails}
        />
      )}
    </div>
  );
};

export default OrganizationListing;